import type { IViewColorConfig, IViewConfig } from "lola-framework-ui-test";

export enum RequestStatus {
  NOT_LOADED,
  lOADING,
  lOADED,
  ERROR,
}

export const errorInitialState = {
  title: null,
  subTitle: null,
  message: null,
  btnText: null,
  isOut: false,
};

export const cardStateInitialState = {
  cardId: null,
  name: null,
  brand: null,
  last4: null,
  accountNumber: null,
  expirationDate: null,
  fullName: null,
  cvv: null,
};

export const themeInitialState = {
  font: {
    h1: {
      fontWeight: "400",
      min: "1.75rem",
      max: "2rem",
      lineHeight: "1",
    },
    highlight: {
      fontWeight: "700",
      min: "1.75rem",
      max: "2rem",
      lineHeight: "0.95",
    },
    h2: {
      fontWeight: "600",
      min: "1.25rem",
      max: "1.5rem",
      lineHeight: "1",
    },
    bodycopy: {
      fontWeight: "500",
      min: "1rem",
      max: "1.25rem",
      lineHeight: "1.25rem",
    },
    secondaryCta: {
      fontWeight: "500",
      min: "0.74rem",
      max: "1rem",
      lineHeight: "1",
    },
    footerText: {
      fontWeight: "500",
      min: "0.85rem",
      max: "0.85rem",
      lineHeight: "1",
    },
    mainButtonText: {
      fontWeight: "600",
      min: "1.15rem",
      max: "1.25rem",
      lineHeight: "2rem",
    },
    step: {
      fontWeight: "600",
      min: "0.5rem",
      max: "0.875rem",
      lineHeight: "1",
    },
    fontfamily: "Helvetica",
    fontcdn: "",
  },
  colors: {
    primaryGradient: "#111111",
    secondaryGradient: "#475268",
    secondaryColor: "#252525",
    whiteColor: "#FFFFFF",
    inactived: "#979797",
    errorColor: "#dc251c",
    partnerHighlights: "#ffffff",
    gradientDeg: "180deg",
    primaryGradientPoint: "0%",
    secundaryGradientPoint: "100%",
    primaryMesh: "linear-gradient(180deg, #111111 0%, #475268 100%)",
    gradient: "linear-gradient(180deg, #111111 0%, #475268 100%)",
    lightness: "dark",
  },
};

const baseView: Omit<IViewColorConfig, "viewConfig"> = {
  background: "#fff",
  iconColors: ["#000", "#000"],
  backgroundIcon: "#fff",
  title: "#000",
  subtitile: "#000",
  bodyCopy: "#000",
  footerColor: "#000",
  backgroundBtn: "#000",
  textColorBtn: "#fff",
  stepsColors: "#000",
  dropzoneColors: ["#000", "#000"],
  stepsLabelColor: "#000",
};

export const themeFormattedInitialState: IViewConfig = {
  primaryMeshGradientView: { ...baseView, viewConfig: "primaryMeshGradientView" },
  specialView: { ...baseView, viewConfig: "specialView" },
  dataView: { ...baseView, viewConfig: "dataView" },
  whiteView: { ...baseView, viewConfig: "whiteView" },
  errorView: { ...baseView, viewConfig: "errorView" },
};

export const APP_ROUTES = {
  LOADING_PAGE: "/",
  LANDING_PAGE: "/landing-page",
  LANDING_PAGE_AGREEMENTS: "/landing-page-agreements",
  CASH_DEPOSIT_LANDING_PAGE: "/cash-deposit-landing",
  CASH_STORES_PAGE: "/cash-stores-page",
  CASH_STORES_BARCODE_EXPIRED_PAGE: "/cash-stores-barcode-expired",
  CASH_STORES_AMOUNT_MISMATCH_PAGE: "/cash-stores-amount-mismatch",
  PAYMENT_VERIFICATION_PAGE: "/payment-verification",
  DEPOSIT_ON_THE_WAY_PAGE: "/deposit-on-the-way",
  STORES_PAGE: "/stores-page",
  STORES_MAP_PAGE: "/stores-map",
  TRANSACTIONS_PAGE: "/transactions-page",
  TRANSFER_PAGE: "/transfer-page",
  ADD_CARD_PAGE: "/add-card-page",
  BILLING_ADDRESS_PAGE: "/billing-address-page",
  SENDING_MONEY_PAGE: "/sending-money-page",
  FUND_REQUEST_PAGE: "/fund-request-page",
  SUPPORT_PAGE: "/support-page",
  ERROR_PAGE: "/error-page",
  SESSION_EXPIRED_PAGE: "/session-expired",
  CANCELLATION_IN_PROGRESS_PAGE: "/cancellation-in-progress",
  SERVICE_UNAVAILABLE_PAGE: "/service-unavailable",
  LEGAL_PAGE: "/legal",
};

/**
 * Routes for the ported "Request" flow gallery screens. Namespaced under
 * `/request/...` so they never collide with the existing screens (which also
 * have Landing / Transfer / Legal / Loading with different content).
 */
export const REQUEST_ROUTES = {
  LOADING: "/request/loading",
  WELCOME: "/request/welcome",
  LANDING: "/request/landing",
  LANDING_RETURNING: "/request/landing-returning",
  HOME: "/request/home",
  INBOX: "/request/inbox",
  PREFERENCES: "/request/preferences",
  TOAST: "/request/toast",
  HOME_RESTRICTED: "/request/home/user-restricted",
  USER_BLOCKED: "/request/user-blocked",
  TRANSFER: "/request/transfer",
  ADD_CARD: "/request/add-card",
  ADD_CARD_PROCESSING: "/request/add-card-processing",
  MINTING: "/request/minting",
  REQUEST_SCREENING: "/request/screening",
  REQUEST_READY: "/request/request-ready",
  RECEIPT: "/request/receipt",
  SUPPORT: "/request/support",
  TERM_DETAIL: "/request/term-detail",
  LEGAL: "/request/legal",
  ERROR: "/request/error",
  SESSION_EXPIRED: "/request/session-expired",
  SERVICE_UNAVAILABLE: "/request/service-unavailable",
  MAINTENANCE: "/request/maintenance",
};

/**
 * Routes for the ported "Cash Deposit" onboarding gallery screens. Namespaced
 * under `/cash-deposit/...` so they never collide with the existing cash-deposit
 * screens registered at root routes (which use different content).
 */
export const CASH_DEPOSIT_ROUTES = {
  CIP_REVIEW: "/cash-deposit/cip-review",
  LOADING: "/cash-deposit/loading",
  WELCOME: "/cash-deposit/welcome",
  TERM_DETAIL: "/cash-deposit/term-detail",
  LEGAL: "/cash-deposit/legal",
  SUPPORT: "/cash-deposit/support",
  USER_BLOCKED: "/cash-deposit/user-blocked",
  HOW_TO_PAY_IN_CASH: "/cash-deposit/how-to-pay-in-cash",
  ERROR: "/cash-deposit/error",
  SESSION_EXPIRED: "/cash-deposit/session-expired",
  SERVICE_UNAVAILABLE: "/cash-deposit/service-unavailable",
  MAINTENANCE: "/cash-deposit/maintenance",
};

/** Heading / title color from the design (not the brand green). */
export const TITLE_COLOR = "#051510";

/**
 * Routes for the ported "Fund Request" gallery screens. Namespaced under
 * `/fund-request/...` so they never collide with the existing screens.
 */
export const FUND_REQUEST_ROUTES = {
  LOADING: "/fund-request/loading",
  WELCOME: "/fund-request/welcome",
  TRANSFER: "/fund-request/transfer",
  TRANSFER_CVV: "/fund-request/transfer/cvv-prepare",
  TRANSACTIONS: "/fund-request/transactions",
  TRANSACTIONS_DEPOSIT_LAUNCHING: "/fund-request/transactions/deposit-launching",
  INBOX: "/fund-request/inbox",
  PREFERENCES: "/fund-request/preferences",
  TOAST: "/fund-request/toast",
  RECEIPT: "/fund-request/receipt",
  CASH_DEPOSIT: "/fund-request/cash-deposit",
  TERM_DETAIL: "/fund-request/term-detail",
  LEGAL: "/fund-request/legal",
  ADD_CARD: "/fund-request/add-card",
  ADD_CARD_FORM_ERRORS: "/fund-request/add-card/form-errors",
  ADD_CARD_BRAND_ERROR: "/fund-request/add-card/brand-not-accepted",
  ADD_CARD_ADDRESS: "/fund-request/add-card-address",
  ADD_CARD_ADDRESS_ERRORS: "/fund-request/add-card-address/form-errors",
  ADD_CARD_PROCESSING: "/fund-request/add-card-processing",
  ADD_CARD_ERROR_VALIDATION: "/fund-request/add-card-error/validation-failed",
  ADD_CARD_ERROR_REVIEW: "/fund-request/add-card-error/under-review",
  ADD_CARD_ERROR_GENERIC: "/fund-request/add-card-error/generic",
  ADD_CARD_ERROR_ALREADY_REGISTERED:
    "/fund-request/add-card-error/already-registered",
  ADD_CARD_ERROR_RATE_LIMIT: "/fund-request/add-card-error/rate-limit",
  TRANSFERRING: "/fund-request/transferring",
  SENDING: "/fund-request/sending",
  HOW_TO_DEPOSIT: "/fund-request/how-to-deposit",
  STORES: "/fund-request/stores",
  SUPPORT: "/fund-request/support",
  SUBJECT_BLOCKED: "/fund-request/subject-blocked",
  ORIGINATOR_BLOCKED: "/fund-request/originator-blocked",
  ALREADY_FULFILLED: "/fund-request/already-fulfilled",
  LATER: "/fund-request/later",
  DISCARD: "/fund-request/discard",
  ROUTE_UNAVAILABLE: "/fund-request/route-unavailable",
  ERROR: "/fund-request/error",
  SESSION_EXPIRED: "/fund-request/session-expired",
  SERVICE_UNAVAILABLE: "/fund-request/service-unavailable",
  MAINTENANCE: "/fund-request/maintenance",
  CANCELLATION: "/fund-request/cancellation",
  CANCELLATION_FAILED: "/fund-request/cancellation-failed",
};

/** Gallery paths that belong to the Fund Request widget. */
export const isFundRequestGalleryPath = (pathname: string): boolean =>
  pathname.startsWith("/fund-request");

/** Gallery paths that belong to the Request (solicitudes) widget. */
export const isRequestGalleryPath = (pathname: string): boolean =>
  pathname.startsWith("/request/");

/** Gallery paths that belong to the Send Money widget. */
export const isSendGalleryPath = (pathname: string): boolean =>
  pathname.startsWith("/send/");

/** Gallery paths that belong to the Lola widget. */
export const isLolaGalleryPath = (pathname: string): boolean =>
  pathname.startsWith("/lola/");

/** Gallery paths that belong to the Deferred Origination widget. */
export const isDeferredOriginationGalleryPath = (pathname: string): boolean =>
  pathname.startsWith("/deferred-origination/");

/** Gallery paths that belong to the Cash Deposit widget. */
export const isCashDepositGalleryPath = (pathname: string): boolean =>
  pathname.startsWith("/cash-deposit/") ||
  pathname === APP_ROUTES.CASH_DEPOSIT_LANDING_PAGE ||
  pathname === APP_ROUTES.CASH_STORES_PAGE ||
  pathname === APP_ROUTES.CASH_STORES_BARCODE_EXPIRED_PAGE ||
  pathname === APP_ROUTES.CASH_STORES_AMOUNT_MISMATCH_PAGE ||
  pathname === APP_ROUTES.PAYMENT_VERIFICATION_PAGE ||
  pathname === APP_ROUTES.DEPOSIT_ON_THE_WAY_PAGE ||
  pathname === APP_ROUTES.STORES_PAGE ||
  pathname === APP_ROUTES.STORES_MAP_PAGE;

/** Gallery paths that belong to the M2M widget. */
export const isM2mGalleryPath = (pathname: string): boolean =>
  pathname.startsWith("/m2m/");

/** Gallery paths that belong to the Starter scaffold. */
export const isStarterGalleryPath = (pathname: string): boolean =>
  pathname.startsWith("/starter/");

/** Gallery paths that belong to the Payout Claim widget. */
export const isPayoutClaimGalleryPath = (pathname: string): boolean =>
  pathname.startsWith("/payout-claim/");

/** Screens sharing the Fund Request compose pack (CTA 60px + 64px rows). */
export const isComposeV2GalleryPath = (pathname: string): boolean =>
  isFundRequestGalleryPath(pathname);

/** Namespaced Error / Session expired / Service unavailable / Cancellation. */
const isNamespacedErrorPath = (pathname: string): boolean =>
  /\/(error|session-expired|service-unavailable|cancellation)(\/|$)/.test(
    pathname,
  );

/** Flow-specific gallery screen class (CTA tokens, row chrome, etc.). */
export const galleryFlowScreenClass = (pathname: string): string => {
  const flow = isComposeV2GalleryPath(pathname)
    ? "gallery-screen--fund-request"
    : isRequestGalleryPath(pathname)
      ? "gallery-screen--request"
      : isSendGalleryPath(pathname)
        ? "gallery-screen--send"
        : isLolaGalleryPath(pathname)
          ? "gallery-screen--lola"
          : isDeferredOriginationGalleryPath(pathname)
            ? "gallery-screen--deferred-origination"
            : isPayoutClaimGalleryPath(pathname)
              ? "gallery-screen--payout-claim"
              : isCashDepositGalleryPath(pathname)
                ? "gallery-screen--cash-deposit"
                : isM2mGalleryPath(pathname)
                  ? "gallery-screen--m2m"
                  : isStarterGalleryPath(pathname)
                    ? "gallery-screen--starter"
                    : "";
  const errorClass = isNamespacedErrorPath(pathname)
    ? "gallery-screen--errors"
    : "";
  return [flow, errorClass].filter(Boolean).join(" ");
};

/**
 * Gallery screens for Consumer Clerk auth (fund-request / payout-claim).
 * Static variants of AuthPage — no Clerk, no network.
 */
export const CONSUMER_AUTH_ROUTES = {
  SIGN_IN: "/consumer-auth/sign-in",
  SIGN_IN_AFTER_LOGOUT: "/consumer-auth/sign-in-after-logout",
  SIGN_IN_BIND_FAILED: "/consumer-auth/sign-in-bind-failed",
  CODE: "/consumer-auth/code",
  CODE_RESEND: "/consumer-auth/code-resend",
  CODE_INCORRECT: "/consumer-auth/code-incorrect",
  BINDING: "/consumer-auth/binding",
  MISSING_KEY: "/consumer-auth/missing-key",
  PENDING: "/consumer-auth/pending",
  ERROR_BIND: "/consumer-auth/error-bind",
  ERROR_GENERIC: "/consumer-auth/error-generic",
};

/**
 * Routes for the ported "Payout Claim" gallery screens.
 */
export const PAYOUT_CLAIM_ROUTES = {
  LOADING: "/payout-claim/loading",
  WELCOME: "/payout-claim/welcome",
  CLAIM_ENTRY: "/payout-claim/claim-entry",
  TRANSFER: "/payout-claim/transfer",
  TRANSFER_PREPARE_ERROR: "/payout-claim/transfer/prepare-error",
  TRANSACTIONS: "/payout-claim/transactions",
  INBOX: "/payout-claim/inbox",
  PREFERENCES: "/payout-claim/preferences",
  TOAST: "/payout-claim/toast",
  RECEIPT: "/payout-claim/receipt",
  CASH_DEPOSIT: "/payout-claim/cash-deposit",
  TERM_DETAIL: "/payout-claim/term-detail",
  LEGAL: "/payout-claim/legal",
  ADD_CARD: "/payout-claim/add-card",
  ADD_CARD_PROCESSING: "/payout-claim/add-card-processing",
  ADD_CARD_ERROR_VALIDATION: "/payout-claim/add-card-error/validation-failed",
  ADD_CARD_ERROR_PUSH: "/payout-claim/add-card-error/push-not-supported",
  ADD_CARD_ERROR_REVIEW: "/payout-claim/add-card-error/under-review",
  CLAIMING: "/payout-claim/claiming",
  SENDING_MONEY: "/payout-claim/sending-money",
  OFAC: "/payout-claim/ofac-disambiguation",
  SUPPORT: "/payout-claim/support",
  CLAIM_BLOCKED: "/payout-claim/claim-blocked",
  SUBJECT_BLOCKED: "/payout-claim/subject-blocked",
  SENDER_BLOCKED: "/payout-claim/sender-blocked",
  LATER: "/payout-claim/later",
  DISCARD: "/payout-claim/discard",
  ERROR: "/payout-claim/error",
  SESSION_EXPIRED: "/payout-claim/session-expired",
  SERVICE_UNAVAILABLE: "/payout-claim/service-unavailable",
  MAINTENANCE: "/payout-claim/maintenance",
};

/**
 * Routes for the ported "Deferred Payout Origination" gallery screens.
 */
export const DEFERRED_ORIGINATION_ROUTES = {
  LOADING: "/deferred-origination/loading",
  WELCOME: "/deferred-origination/welcome",
  LANDING: "/deferred-origination/landing",
  LANDING_RETURNING: "/deferred-origination/landing-returning",
  HOME: "/deferred-origination/home",
  INBOX: "/deferred-origination/inbox",
  PREFERENCES: "/deferred-origination/preferences",
  TOAST: "/deferred-origination/toast",
  HOME_DEPOSIT_LAUNCHING: "/deferred-origination/home/deposit-launching",
  HOME_RESTRICTED: "/deferred-origination/home/user-restricted",
  USER_BLOCKED: "/deferred-origination/user-blocked",
  TRANSFER: "/deferred-origination/transfer",
  RECIPIENT: "/deferred-origination/recipient",
  RECIPIENT_CVV: "/deferred-origination/recipient/cvv-prepare",
  ADD_CARD: "/deferred-origination/add-card",
  ADD_CARD_ADDRESS: "/deferred-origination/add-card-address",
  ADD_CARD_PROCESSING: "/deferred-origination/add-card-processing",
  MINTING: "/deferred-origination/minting",
  HOW_TO_DEPOSIT: "/deferred-origination/how-to-deposit",
  STORES: "/deferred-origination/stores",
  REQUEST_READY: "/deferred-origination/request-ready",
  RECEIPT: "/deferred-origination/receipt",
  SUPPORT: "/deferred-origination/support",
  TERM_DETAIL: "/deferred-origination/term-detail",
  LEGAL: "/deferred-origination/legal",
  ERROR: "/deferred-origination/error",
  SESSION_EXPIRED: "/deferred-origination/session-expired",
  SERVICE_UNAVAILABLE: "/deferred-origination/service-unavailable",
  MAINTENANCE: "/deferred-origination/maintenance",
  CANCELLATION: "/deferred-origination/cancellation",
  CANCELLATION_FAILED: "/deferred-origination/cancellation-failed",
};

/**
 * Routes for the ported m2m gallery screens that don't yet exist at root.
 * Namespaced under `/m2m/...`.
 */
export const M2M_ROUTES = {
  LOADING: "/m2m/loading",
  WELCOME: "/m2m/welcome",
  TERM_DETAIL: "/m2m/term-detail",
  LEGAL: "/m2m/legal",
  LANDING: "/m2m/landing",
  LANDING_B: "/m2m/landing-b",
  TRANSACTIONS: "/m2m/transactions",
  INBOX: "/m2m/inbox",
  PREFERENCES: "/m2m/preferences",
  TOAST: "/m2m/toast",
  TRANSACTIONS_RESTRICTED: "/m2m/transactions/user-restricted",
  USER_BLOCKED: "/m2m/user-blocked",
  TRANSFER_A: "/m2m/transfer-a",
  TRANSFER_B: "/m2m/transfer-b",
  TRANSFER_C: "/m2m/transfer-c",
  SUPPORT_V2: "/m2m/support-v2",
  ADD_CARD: "/m2m/add-card",
  ADD_CARD_ADDRESS: "/m2m/add-card-address",
  ADD_CARD_PROCESSING: "/m2m/add-card-processing",
  ADD_CARD_COLLECT: "/m2m/add-card-collect",
  RECEIPT: "/m2m/receipt",
  SENDING: "/m2m/sending",
  SENDING_B: "/m2m/sending-b",
  ERROR: "/m2m/error",
  SESSION_EXPIRED: "/m2m/session-expired",
  SERVICE_UNAVAILABLE: "/m2m/service-unavailable",
  MAINTENANCE: "/m2m/maintenance",
};

/**
 * Routes for the ported "Starter" scaffold gallery screens. Namespaced under
 * `/starter/...`.
 */
export const STARTER_ROUTES = {
  LOADING: "/starter/loading",
  WELCOME: "/starter/welcome",
  LANDING: "/starter/landing",
  TERM_DETAIL: "/starter/term-detail",
  LEGAL: "/starter/legal",
  ROTATE_DEVICE: "/starter/rotate-device",
  ERROR: "/starter/error",
  SESSION_EXPIRED: "/starter/session-expired",
  SERVICE_UNAVAILABLE: "/starter/service-unavailable",
  MAINTENANCE: "/starter/maintenance",
};

/**
 * Routes for the ported "Send Money" gallery screens. Namespaced under
 * `/send/...` so they never collide with existing screens.
 */
export const SEND_ROUTES = {
  LOADING: "/send/loading",
  WELCOME: "/send/welcome",
  LANDING: "/send/landing",
  LANDING_RETURNING: "/send/landing-returning",
  HOME: "/send/home",
  INBOX: "/send/inbox",
  PREFERENCES: "/send/preferences",
  TOAST: "/send/toast",
  HOME_DEPOSIT_LAUNCHING: "/send/home/deposit-launching",
  HOME_RESTRICTED: "/send/home/user-restricted",
  USER_BLOCKED: "/send/user-blocked",
  TRANSFER: "/send/transfer",
  TRANSFER_CVV: "/send/transfer/cvv-prepare",
  ADD_CARD: "/send/add-card",
  ADD_CARD_ADDRESS: "/send/add-card-address",
  ADD_CARD_PROCESSING: "/send/add-card-processing",
  SENDING: "/send/sending",
  HOW_TO_DEPOSIT: "/send/how-to-deposit",
  STORES: "/send/stores",
  CASH_DEPOSIT: "/send/cash-deposit",
  RECEIPT: "/send/receipt",
  SUPPORT: "/send/support",
  TERM_DETAIL: "/send/term-detail",
  LEGAL: "/send/legal",
  ERROR: "/send/error",
  SESSION_EXPIRED: "/send/session-expired",
  SERVICE_UNAVAILABLE: "/send/service-unavailable",
  MAINTENANCE: "/send/maintenance",
  CANCELLATION: "/send/cancellation",
  CANCELLATION_FAILED: "/send/cancellation-failed",
};

/**
 * Routes for the ported "Smart" (Lola Send) gallery screens. Namespaced under
 * `/smart/...`. Keeps Smart's Tailwind/Framer UI kit — not AuraLayout.
 */
/**
 * Routes for the Lola (WhatsApp handoff) gallery screens. Namespaced under
 * `/lola/...`. Splash → Welcome → certified confirmation, then the
 * fund-request card / commit path.
 */
export const LOLA_ROUTES = {
  LOADING: "/lola/loading",
  WELCOME: "/lola/welcome",
  TERM_DETAIL: "/lola/term-detail",
  LEGAL: "/lola/legal",
  CONFIRMATION: "/lola/confirmation",
  CONFIRMATION_CVV: "/lola/confirmation-cvv",
  HOW_TO_CASH_DEPOSIT: "/lola/how-to-cash-deposit",
  ADD_CARD: "/lola/add-card",
  ADD_CARD_ADDRESS: "/lola/add-card-address",
  ADD_CARD_PROCESSING: "/lola/add-card-processing",
  CIP: "/lola/cip",
  TRANSFERRING: "/lola/transferring",
  SENDING: "/lola/sending",
  ROUTE_UNAVAILABLE: "/lola/route-unavailable",
  LINK_ALREADY_USED: "/lola/link-already-used",
  ERROR: "/lola/error",
  SESSION_EXPIRED: "/lola/session-expired",
  SERVICE_UNAVAILABLE: "/lola/service-unavailable",
  MAINTENANCE: "/lola/maintenance",
};

export const SMART_ROUTES = {
  LOADING: "/smart/loading",
  HOME: "/smart/home",
  INBOX: "/smart/inbox",
  PREFERENCES: "/smart/preferences",
  TOAST: "/smart/toast",
  WIZARD_INTENT: "/smart/wizard/intent",
  WIZARD_ROUTE: "/smart/wizard/route",
  WIZARD_FUNDING: "/smart/wizard/funding",
  WIZARD_PAYOUT: "/smart/wizard/payout",
  COLLECT_RECIPIENT: "/smart/collect/recipient",
  COLLECT_SUMMARY: "/smart/collect/summary",
  SMS_TERMS: "/smart/sms-terms",
  SMS_TERMS_DETAIL: "/smart/sms-terms/detail",
  /** CIP / KYC gate + headless step shells (static — no Kentucky / AWS). */
  KYC_CHECKING: "/smart/kyc/checking",
  KYC_INTRO: "/smart/kyc/intro",
  KYC_ERROR: "/smart/kyc/error",
  CIP_CLAIMING: "/smart/cip/claiming",
  CIP_SCAN_ID: "/smart/cip/scan-id",
  CIP_SCAN_ID_LOADING: "/smart/cip/scan-id-loading",
  CIP_IDENTITY_REVIEW: "/smart/cip/identity-review",
  CIP_ADDRESS: "/smart/cip/address",
  CIP_LIVENESS: "/smart/cip/liveness",
  CIP_LIVENESS_CAPTURE: "/smart/cip/liveness-capture",
  CIP_LIVENESS_RETRY: "/smart/cip/liveness-retry",
  CIP_COMPLETION: "/smart/cip/completion",
  CIP_COMPLETION_SUCCESS: "/smart/cip/completion-success",
  COMMITTING: "/smart/committing",
  DONE_SEND: "/smart/done/send",
  DONE_SHARE: "/smart/done/share",
  MERCHANT_PICKER: "/smart/merchant-picker",
  BARCODE: "/smart/barcode",
  ERROR: "/smart/error",
  CLAIM_FAIL: "/smart/claim-fail",
  CLAIM_FAIL_RETURN: "/smart/claim-fail-return",
  MAINTENANCE: "/smart/maintenance",
};

/** Gallery mock of card-funding origins (INT-07). Two codes so the country selector is visible. */
export const GALLERY_CARD_ORIGIN_COUNTRIES: string[] = ["US", "MX"];

export const US_STATES = [
  { code: "AL", name: "Alabama" },
  { code: "AK", name: "Alaska" },
  { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" },
  { code: "CA", name: "California" },
  { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" },
  { code: "DE", name: "Delaware" },
  { code: "DC", name: "District of Columbia" },
  { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" },
  { code: "HI", name: "Hawaii" },
  { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" },
  { code: "IN", name: "Indiana" },
  { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" },
  { code: "KY", name: "Kentucky" },
  { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" },
  { code: "MD", name: "Maryland" },
  { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" },
  { code: "MN", name: "Minnesota" },
  { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" },
  { code: "MT", name: "Montana" },
  { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" },
  { code: "NH", name: "New Hampshire" },
  { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" },
  { code: "NY", name: "New York" },
  { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" },
  { code: "OH", name: "Ohio" },
  { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" },
  { code: "PA", name: "Pennsylvania" },
  { code: "PR", name: "Puerto Rico" },
  { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" },
  { code: "SD", name: "South Dakota" },
  { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" },
  { code: "UT", name: "Utah" },
  { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" },
  { code: "VI", name: "Virgin Islands" },
  { code: "WA", name: "Washington" },
  { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" },
  { code: "WY", name: "Wyoming" },
] as const;

export const cardNumberPlaceHolder = "XXXX XXXX XXXX XXXX";
export const validCards = "Visa & Mastercard";